import { sortOrganisations } from "@/utils/organisationSort";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ColorPicker } from "@/components/design/ColorPicker";
import { useToast } from "@/hooks/use-toast";
import { useSuperuserGuard } from "@/hooks/use-superuser-guard";
import { supabase } from "@/integrations/supabase/client";
import { BRAND_FONTS, BRAND_FONT_WEIGHTS, ensureClashGrotesk, getFontStack, getSupportedBrandFont, getSupportedBrandFontWeight, isGoogleBrandFont } from "@/lib/brand/fontCatalog";
import { BrandStudioPreview } from "@/components/brand/BrandStudioPreview";
import { BrandImageLinkInput } from "@/components/brand/BrandImageLinkInput";
import { getTitleGraphicPosition } from "@/lib/brand/titleGraphic";
import type { BrandConfig } from "@/contexts/BrandContext";
import { getBrandLogoScale, getEmailLogoSize, MAX_LOGO_SCALE, MIN_LOGO_SCALE } from "@/lib/brand/logoScale";
import { Plus, ExternalLink, Rocket, Save, Palette, PaintBucket, Type, Image, Layers3, Trash2, Rows3, Grid2X2, LayoutDashboard, Images, AlignLeft, AlignCenter, AlignRight, AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface BrandRow {
  id: string;
  organization_id: string | null;
  subdomain: string;
  status: "draft" | "live";
  is_active: boolean;
  published: Record<string, any>;
  draft: Record<string, any>;
  email_domain_verified: boolean;
  allow_indexing: boolean;
}

const COLOUR_GROUPS = [
  { label: "Brand actions", description: "Buttons, links and highlights", fields: [
    { key: "primary", label: "Main colour", fallback: "335 41% 14%" },
    { key: "primary-foreground", label: "Button text", fallback: "0 0% 100%" },
    { key: "primary-hover", label: "Button hover (optional)", fallback: "71 73% 86%" },
    { key: "accent", label: "Accent", fallback: "71 73% 76%" },
  ]},
  { label: "Page and text", description: "The main canvas and readable content", fields: [
    { key: "background", label: "Page background", fallback: "0 0% 100%" },
    { key: "heading-foreground", label: "Heading text", fallback: "335 41% 14%" },
    { key: "subheading-foreground", label: "Subheading text", fallback: "335 41% 14%" },
    { key: "foreground", label: "Body text", fallback: "335 41% 14%" },
  ]},
];

const HIGHLIGHT_LAYOUTS = [
  { value: "alternating", label: "Alternating rows", icon: Rows3 },
  { value: "cards", label: "Card grid", icon: Grid2X2 },
  { value: "featured", label: "Featured grid", icon: LayoutDashboard },
  { value: "gallery", label: "Image gallery", icon: Images },
] as const;

const HIGHLIGHT_SPACING = [
  { value: "compact", label: "Compact" },
  { value: "standard", label: "Standard" },
  { value: "spacious", label: "Spacious" },
] as const;

const HIGHLIGHT_ALIGNMENTS = [
  { value: "left", label: "Align left", icon: AlignLeft },
  { value: "center", label: "Align centre", icon: AlignCenter },
  { value: "right", label: "Align right", icon: AlignRight },
] as const;

const GRAPHIC_POSITIONS = [
  { value: "top", label: "Align graphic top", icon: AlignVerticalJustifyStart },
  { value: "center", label: "Align graphic centre", icon: AlignVerticalJustifyCenter },
  { value: "bottom", label: "Align graphic bottom", icon: AlignVerticalJustifyEnd },
] as const;

const escHtml = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));

/** Mirrors the branded email layout sent by the email service (email-safe inline styles). */
type EmailKind = "invite" | "magiclink" | "recovery" | "camera_alert" | "camera_image";
function emailPreviewHtml(form: Record<string, any>, kind: EmailKind = "recovery", defaultCameraCopy?: Record<string, any>): string {
  const c = form.color_tokens || {};
  const col = (v: string | undefined, fb: string) => (v ? (v.trim().startsWith("#") ? v : `hsl(${v})`) : fb);
  const primary = col(c.primary, "#321521");
  const accent = col(c.accent, "#DFEF97");
  const bg = col(c.background, "#DFD4C9");
  const buttonText = col(c["primary-foreground"], "#ffffff");
  const bodyText = col(c.foreground, "#321521");
  const headingText = col(c["heading-foreground"], bodyText);
  const subheadingText = col(c["subheading-foreground"], bodyText);
  const name = escHtml(form.company_name || "Your brand");
  const logo = form.email?.logo_url || form.logo_url;
  const logoSize = getEmailLogoSize(form.logo_scale);
  const headingFont = getSupportedBrandFont(form.font_config?.heading) || "Arial";
  const bodyFont = getSupportedBrandFont(form.font_config?.body) || "Arial";
  const headingWeight = getSupportedBrandFontWeight(form.font_config?.heading_weight, "700");
  const bodyWeight = getSupportedBrandFontWeight(form.font_config?.body_weight, "400");
  const hf = `'${escHtml(headingFont)}', Arial, sans-serif`;
  const bf = `'${escHtml(bodyFont)}', Arial, sans-serif`;
  const googleFonts = Array.from(new Set([headingFont, bodyFont].filter(isGoogleBrandFont)));
  const fontLinks = googleFonts.map((family) => {
    const weights = family === headingFont && family === bodyFont
      ? Array.from(new Set([headingWeight, bodyWeight])).sort().join(";")
      : family === headingFont ? headingWeight : bodyWeight;
    return `<link href="https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@${weights}&display=swap" rel="stylesheet">`;
  }).join("");
  const footer = escHtml(form.email?.footer_text || `${form.company_name || "Your brand"}${form.email?.reply_to || form.support_email ? ` · ${form.email?.reply_to || form.support_email}` : ""}`);
  const copy = {
    recovery: { title: "Reset your password", body: `We received a request to reset the password for your ${name} account.`, cta: "Reset password", note: "This link expires soon. If you didn't ask for this, you can ignore this email." },
    magiclink: { title: `Sign in to ${name}`, body: `Click the button below to sign in to your ${name} account. No password needed.`, cta: "Sign in", note: "This link can only be used once and expires soon. If you didn't ask for it, you can ignore this email." },
    invite: { title: `You're invited to join Example Customer on ${name}`, body: `You've been invited to join Example Customer on ${name}. Create your account to see your cameras.`, cta: "Accept invitation", note: "This invitation expires in 7 days." },
    camera_alert: { title: "Vehicle blocking site gate", body: "A vehicle has been parked across the main gate for over 10 minutes.", cta: "View camera", note: "Camera: North Gate · Time: 14:32 · Stop these alerts" },
    camera_image: { title: "Site clear, work progressing", body: "No issues detected in the latest image.", cta: "View camera", note: "Camera: North Gate · Time: 14:32 · Stop these alerts" },
  }[kind];
  const isCamera = kind === "camera_alert" || kind === "camera_image";
  const cc: Record<string, any> = isCamera ? { ...(defaultCameraCopy || {}), ...(form.email?.[kind] || {}) } : {};
  const ccText = (k: string) => (typeof cc[k] === "string" && cc[k].trim() ? escHtml(cc[k].trim().replace(/\{\{\s*camera_name\s*\}\}/g, "North Gate").replace(/\{\{\s*organisation\s*\}\}/g, form.company_name || "Your brand")) : "");
  const badgeBackground = col(cc.badge_background, accent);
  const badgeTextColor = col(cc.badge_text_color, buttonText);
  if (isCamera) {
    copy.cta = ccText("cta_text") || copy.cta;
    copy.note = `Camera: North Gate · Time: 14:32 · ${ccText("unsubscribe_text") || "Stop these alerts"}`;
  }
  const cameraIntro = isCamera && ccText("intro") ? `<p style="font-family:${bf};font-size:15px;line-height:1.6;margin:0 0 12px;color:${bodyText};">${ccText("intro")}</p>` : "";
  const cameraFooter = isCamera ? ccText("footer") : "";
  const cameraBlock = isCamera
    ? `<table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:0 0 12px;"><tr><td bgcolor="${badgeBackground}" style="background:${badgeBackground};color:${badgeTextColor};font-family:${bf};font-size:12px;font-weight:700;padding:4px 10px;border-radius:12px;">${ccText("badge_text") || (kind === "camera_alert" ? "ALERT" : "NEW IMAGE")}</td></tr></table>`
    : "";
  const cameraImage = isCamera && cc.show_image !== false
    ? `<div style="height:220px;border-radius:8px;margin:0 0 20px;background:${accent};text-align:center;line-height:220px;color:${primary};font-family:${bf};font-size:13px;">Latest camera image</div>`
    : "";
  const cameraReason = isCamera ? `<p style="font-family:${bf};font-size:14px;line-height:1.6;margin:0 0 20px;color:${bodyText};"><strong>Notification reason:</strong> ${kind === "camera_alert" ? "The gate is obstructed and needs attention." : "The latest image confirms the site is clear."}</p>` : "";
  return `<!DOCTYPE html><html><head><meta charset="utf-8">${fontLinks}</head><body style="margin:0;background:${bg};"><div style="max-width:600px;margin:0 auto;font-family:${bf};font-weight:${bodyWeight};background:${bg};color:${bodyText};">
  <div style="background:${bg};padding:28px 24px;text-align:center;">${logo ? `<img src="${escHtml(logo)}" style="max-height:${logoSize.height}px;max-width:${logoSize.width}px;width:auto;height:auto;display:block;margin:0 auto;" />` : `<div style="font-family:${hf};font-size:22px;font-weight:${headingWeight};color:${primary};">${name}</div>`}</div>
<div style="padding:32px 28px;color:${bodyText};">${cameraBlock}<h1 style="font-family:${hf};font-weight:${headingWeight};font-size:24px;margin:0 0 16px;color:${headingText};">${copy.title}</h1>
 ${cameraIntro}<p style="font-family:${bf};font-weight:${bodyWeight};font-size:15px;line-height:1.6;margin:0 0 ${isCamera ? "12" : "28"}px;color:${bodyText};">${copy.body}</p>${cameraReason}
${cameraImage}
 <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin:0 auto 28px;"><tr><td bgcolor="${primary}" style="background:${primary};border-bottom:3px solid ${accent};border-radius:8px;"><span style="display:inline-block;background:${primary};color:${buttonText};font-family:${bf};font-weight:${bodyWeight};padding:14px 32px;border-radius:8px;">${copy.cta}</span></td></tr></table>
<p style="font-family:${bf};font-weight:${bodyWeight};font-size:13px;color:${subheadingText};">${copy.note}</p></div>
<div style="border-top:1px solid ${subheadingText};padding:16px 24px;text-align:center;font-family:${bf};font-weight:${bodyWeight};font-size:12px;color:${bodyText};">${cameraFooter || footer}</div></div></body></html>`;
}

function hslToHex(value: string) {
  const match = value.trim().match(/^(-?[\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!match) return "#321521";
  const h = ((Number(match[1]) % 360) + 360) % 360;
  const s = Number(match[2]) / 100;
  const l = Number(match[3]) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return `#${[r, g, b].map((channel) => Math.round((channel + m) * 255).toString(16).padStart(2, "0")).join("")}`;
}

function hexToHsl(hex: string) {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  return `${Math.round((h + 360) % 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function relativeLuminance(hsl: string) {
  const hex = hslToHex(hsl).slice(1);
  const channels = [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255)
    .map((channel) => channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4));
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

function contrastRatio(first: string, second: string) {
  const a = relativeLuminance(first);
  const b = relativeLuminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

const db = () => (supabase as any).from("white_label_brands");

export default function WhiteLabelStudio() {
  const { isLoading: guardLoading, isSuperuser } = useSuperuserGuard();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, any>>({});
  const [newSubdomain, setNewSubdomain] = useState("");
  const [previewViewport, setPreviewViewport] = useState<"desktop" | "mobile">("desktop");
  const [emailKind, setEmailKind] = useState<EmailKind>("invite");
  const [uploadCount, setUploadCount] = useState(0);
  const onUploadingChange = (uploading: boolean) => setUploadCount((count) => Math.max(0, count + (uploading ? 1 : -1)));

  useEffect(() => {
    ensureClashGrotesk();
  }, []);

  const { data: brands = [] } = useQuery<BrandRow[]>({
    queryKey: ["white-label-brands"],
    queryFn: async () => {
      const { data, error } = await db().select("*").order("subdomain");
      if (error) throw error;
      return (data || []) as BrandRow[];
    },
    enabled: isSuperuser,
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations-for-brands"],
    queryFn: async () => {
      const { data, error } = await supabase.from("organizations").select("id, name").order("name");
      if (error) throw error;
      return data || [];
    },
    enabled: isSuperuser,
  });

  const { data: defaultCameraCopy = {} } = useQuery<Record<string, Record<string, any>>>({
    queryKey: ["camera-email-badge-defaults"],
    queryFn: async () => {
      const { data, error } = await supabase.from("email_templates")
        .select("template_type, content_config")
        .in("template_type", ["camera_alert", "camera_image"])
        .eq("is_active", true)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data || []).reduce((acc, row) => {
        if (!acc[row.template_type]) acc[row.template_type] = (row.content_config || {}) as Record<string, any>;
        return acc;
      }, {} as Record<string, Record<string, any>>);
    },
    enabled: isSuperuser,
  });

  const selected = useMemo(
    () => brands.find((b) => b.id === selectedId) || null,
    [brands, selectedId]
  );

  useEffect(() => {
    if (!selectedId && brands.length) setSelectedId(brands[0].id);
  }, [brands, selectedId]);

  useEffect(() => {
    if (selected) setForm(JSON.parse(JSON.stringify(selected.draft || {})));
  }, [selected?.id]);

  const set = (path: string, value: any) =>
    setForm((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let node: any = next;
      for (let i = 0; i < parts.length - 1; i++) {
        node[parts[i]] = { ...(node[parts[i]] || {}) };
        node = node[parts[i]];
      }
      node[parts[parts.length - 1]] = value;
      return next;
    });

  const createBrand = useMutation({
    mutationFn: async () => {
      const sub = newSubdomain.trim().toLowerCase();
      if (!/^[a-z0-9-]{2,40}$/.test(sub)) throw new Error("Use lowercase letters, numbers and dashes only");
      const { data, error } = await db()
        .insert({ subdomain: sub, draft: { company_name: sub } })
        .select()
        .single();
      if (error) throw error;
      return data as BrandRow;
    },
    onSuccess: (row) => {
      setNewSubdomain("");
      queryClient.invalidateQueries({ queryKey: ["white-label-brands"] });
      setSelectedId(row.id);
      toast({ description: "Brand created" });
    },
    onError: (e: any) => toast({ description: e.message || "Could not create brand", variant: "destructive" }),
  });

  const saveDraft = useMutation({
    mutationFn: async () => {
      if (!selected) return;
      const { error } = await db().update({ draft: form, updated_at: new Date().toISOString() }).eq("id", selected.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["white-label-brands"] });
      toast({ description: "Draft saved" });
    },
    onError: (e: any) => toast({ description: e.message || "Could not save", variant: "destructive" }),
  });

  const publish = useMutation({
    mutationFn: async () => {
      if (!selected) return;
      const { error } = await db()
        .update({
          published: form,
          draft: form,
          status: "live",
          is_active: true,
          published_at: new Date().toISOString(),
        })
        .eq("id", selected.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["white-label-brands"] });
      toast({ description: "Brand published" });
    },
    onError: (e: any) => toast({ description: e.message || "Could not publish", variant: "destructive" }),
  });

  const updateRow = useMutation({
    mutationFn: async (patch: Record<string, any>) => {
      if (!selected) return;
      const { error } = await db().update(patch).eq("id", selected.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["white-label-brands"] }),
  });

  const features: any[] = Array.isArray(form?.landing_content?.features) ? form.landing_content.features : [];
  const setFeatures = (list: any[]) => set("landing_content.features", list);
  const previewConfig = form as BrandConfig;
  const buttonContrast = contrastRatio(form.color_tokens?.primary || "335 41% 14%", form.color_tokens?.["primary-foreground"] || "0 0% 100%");
  const pageBackground = form.color_tokens?.background || "0 0% 100%";
  const bodyColor = form.color_tokens?.foreground || "335 41% 14%";
  const headingColor = form.color_tokens?.["heading-foreground"] || bodyColor;
  const subheadingColor = form.color_tokens?.["subheading-foreground"] || bodyColor;
  const bodyContrast = contrastRatio(pageBackground, bodyColor);
  const headingContrast = contrastRatio(pageBackground, headingColor);
  const subheadingContrast = contrastRatio(pageBackground, subheadingColor);

  if (guardLoading) return null;
  if (!isSuperuser) return null;

  const previewUrl = selected ? `/?brand=${selected.subdomain}&preview=draft` : "#";

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <main className="flex-1 p-6">
          <div className="space-y-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Palette className="h-6 w-6" /> Brand Studio
                </h1>
                <p className="text-muted-foreground text-sm">
                  Create a branded site and portal for a partner, on their own web address.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="new address e.g. kaarbontech"
                  value={newSubdomain}
                  onChange={(e) => setNewSubdomain(e.target.value)}
                  className="w-56"
                />
                <Button onClick={() => createBrand.mutate()} disabled={!newSubdomain.trim()}>
                  <Plus className="h-4 w-4 mr-1" /> Add brand
                </Button>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
              <Card>
                <CardHeader><CardTitle className="text-base">Brands</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {brands.length === 0 && <p className="text-sm text-muted-foreground">No brands yet.</p>}
                  {brands.map((b) => (
                    <button
                      key={b.id}
                      disabled={uploadCount > 0}
                      onClick={() => setSelectedId(b.id)}
                      className={`w-full text-left rounded-md border px-3 py-2 text-sm ${
                        b.id === selectedId ? "border-primary bg-muted" : ""
                      }`}
                    >
                      <div className="font-medium">{b.draft?.company_name || b.subdomain}</div>
                      <div className="text-xs text-muted-foreground">{b.subdomain}.snapshot.live</div>
                      <Badge variant={b.status === "live" && b.is_active ? "default" : "secondary"} className="mt-1">
                        {b.status === "live" && b.is_active ? "Live" : "Draft"}
                      </Badge>
                    </button>
                  ))}
                </CardContent>
              </Card>

              {selected && (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" disabled={uploadCount > 0 || saveDraft.isPending} onClick={() => saveDraft.mutate()}>
                      <Save className="h-4 w-4 mr-1" /> Save draft
                    </Button>
                    <Button disabled={uploadCount > 0 || publish.isPending} onClick={() => publish.mutate()}>
                      <Rocket className="h-4 w-4 mr-1" /> Publish
                    </Button>
                    <Button variant="outline" asChild>
                      <a href={previewUrl} target="_blank" rel="noopener">
                        <ExternalLink className="h-4 w-4 mr-1" /> Preview draft
                      </a>
                    </Button>
                    <div className="flex items-center gap-2 ml-auto text-sm">
                      <Switch
                        checked={selected.is_active}
                        onCheckedChange={(v) => updateRow.mutate({ is_active: v })}
                      />
                      <span>Visible to visitors</span>
                    </div>
                  </div>

                  <Tabs defaultValue="basics">
                    <TabsList>
                      <TabsTrigger value="basics">Basics</TabsTrigger>
                      <TabsTrigger value="look">Colours & fonts</TabsTrigger>
                      <TabsTrigger value="landing">Landing page</TabsTrigger>
                      <TabsTrigger value="emails">Emails</TabsTrigger>
                    </TabsList>

                    <TabsContent value="basics" className="space-y-4 pt-4">
                      <Card><CardContent className="grid gap-4 pt-6 md:grid-cols-2">
                        <div>
                          <Label>Company name</Label>
                          <Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} />
                        </div>
                        <div>
                          <Label>Support email</Label>
                          <Input value={form.support_email || ""} onChange={(e) => set("support_email", e.target.value)} />
                        </div>
                        <div>
                          <Label>Organisation</Label>
                          <Select
                            value={selected.organization_id || ""}
                            onValueChange={(v) => updateRow.mutate({ organization_id: v })}
                          >
                            <SelectTrigger><SelectValue placeholder="Choose organisation" /></SelectTrigger>
                            <SelectContent>
                              {sortOrganisations(organizations, o => o.name).map((o) => (
                                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Web address</Label>
                          <Input value={`${selected.subdomain}.snapshot.live`} readOnly />
                        </div>
                        <div>
                          <Label>Logo image link</Label>
                          <BrandImageLinkInput key={`logo-${selected.id}`} brandId={selected.id} label="Logo" value={form.logo_url || ""} onChange={(url) => set("logo_url", url)} onUploadingChange={onUploadingChange} />
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <Label htmlFor="brand-logo-size">Logo size</Label>
                            <span className="text-sm font-medium tabular-nums">{getBrandLogoScale(form.logo_scale)}%</span>
                          </div>
                          <Slider
                            id="brand-logo-size"
                            min={MIN_LOGO_SCALE}
                            max={MAX_LOGO_SCALE}
                            step={5}
                            value={[getBrandLogoScale(form.logo_scale)]}
                            onValueChange={([value]) => set("logo_scale", value)}
                            aria-label="Logo size"
                          />
                          <p className="text-xs text-muted-foreground">Used across the landing page, portal, sign-in pages, showcase and user emails.</p>
                        </div>
                        <div>
                          <Label>Browser icon link</Label>
                          <BrandImageLinkInput key={`favicon-${selected.id}`} brandId={selected.id} label="Browser icon" allowIcon value={form.favicon_url || ""} onChange={(url) => set("favicon_url", url)} onUploadingChange={onUploadingChange} />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={selected.allow_indexing}
                            onCheckedChange={(v) => updateRow.mutate({ allow_indexing: v })}
                          />
                          <span className="text-sm">Allow search engines to list this site</span>
                        </div>
                      </CardContent></Card>
                    </TabsContent>

                    <TabsContent value="look" className="pt-4">
                      <div className="grid items-start gap-5 xl:grid-cols-[minmax(360px,0.8fr)_minmax(480px,1.2fr)]">
                        <div className="space-y-5">
                          {COLOUR_GROUPS.map((group) => (
                            <section key={group.label} className="brand-studio-section">
                              <div className="mb-4 flex items-start gap-3">
                                <span className="brand-studio-section-icon"><PaintBucket className="h-4 w-4" /></span>
                                <div><h3 className="font-semibold">{group.label}</h3><p className="text-xs text-muted-foreground">{group.description}</p></div>
                              </div>
                              <div className="space-y-3">
                                {group.fields.map((colour) => {
                                  const value = form.color_tokens?.[colour.key] || colour.fallback;
                                  return (
                                    <div key={colour.key} className="brand-colour-row">
                                      <input type="color" value={hslToHex(value)} onChange={(event) => set(`color_tokens.${colour.key}`, hexToHsl(event.target.value))} aria-label={`Choose ${colour.label.toLowerCase()}`} />
                                      <div className="min-w-0 flex-1"><Label>{colour.label}</Label><p className="text-xs text-muted-foreground">{hslToHex(value).toUpperCase()}</p></div>
                                      <Input className="w-32 font-mono text-xs" value={value} onChange={(event) => set(`color_tokens.${colour.key}`, event.target.value)} aria-label={`${colour.label} HSL value`} />
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                                {group.label === "Brand actions" && <Badge variant={buttonContrast >= 4.5 ? "secondary" : "destructive"}>Button contrast {buttonContrast.toFixed(1)}:1</Badge>}
                                {group.label === "Page and text" && <>
                                  <Badge variant={headingContrast >= 4.5 ? "secondary" : "destructive"}>Heading {headingContrast.toFixed(1)}:1</Badge>
                                  <Badge variant={subheadingContrast >= 4.5 ? "secondary" : "destructive"}>Subheading {subheadingContrast.toFixed(1)}:1</Badge>
                                  <Badge variant={bodyContrast >= 4.5 ? "secondary" : "destructive"}>Body {bodyContrast.toFixed(1)}:1</Badge>
                                </>}
                              </div>
                            </section>
                          ))}

                          <section className="brand-studio-section">
                            <div className="mb-4 flex items-start gap-3">
                              <span className="brand-studio-section-icon"><Type className="h-4 w-4" /></span>
                              <div><h3 className="font-semibold">Typography</h3><p className="text-xs text-muted-foreground">Choose a distinct voice for headings and readable body copy.</p></div>
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                              {[
                                { key: "heading", weightKey: "heading_weight", label: "Heading font", fallbackWeight: "700" as const },
                                { key: "body", weightKey: "body_weight", label: "Body font", fallbackWeight: "400" as const },
                              ].map((field) => (
                                <div key={field.key} className="space-y-3">
                                  <div>
                                    <Label>{field.label}</Label>
                                    <Select value={getSupportedBrandFont(form.font_config?.[field.key]) || "Archivo"} onValueChange={(value) => set(`font_config.${field.key}`, value)}>
                                      <SelectTrigger style={{ fontFamily: getFontStack(form.font_config?.[field.key]) }}><SelectValue /></SelectTrigger>
                                      <SelectContent>{BRAND_FONTS.map((font) => <SelectItem key={font.value} value={font.value}><span style={{ fontFamily: getFontStack(font.value) }}>{font.label}</span></SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label>{field.key === "heading" ? "Heading weight" : "Body weight"}</Label>
                                    <Select value={getSupportedBrandFontWeight(form.font_config?.[field.weightKey], field.fallbackWeight)} onValueChange={(value) => set(`font_config.${field.weightKey}`, value)}>
                                      <SelectTrigger><SelectValue /></SelectTrigger>
                                      <SelectContent>{BRAND_FONT_WEIGHTS.map((weight) => <SelectItem key={weight.value} value={weight.value}><span style={{ fontFamily: getFontStack(form.font_config?.[field.key]), fontWeight: weight.value }}>{weight.label} · {weight.value}</span></SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="brand-type-specimen mt-4" style={{ backgroundColor: `hsl(${pageBackground})` }}>
                              <p className="text-xs text-muted-foreground">TYPE SAMPLE</p>
                              <h4 style={{ color: `hsl(${headingColor})`, fontFamily: getFontStack(form.font_config?.heading), fontWeight: getSupportedBrandFontWeight(form.font_config?.heading_weight, "700") }}>Built to go where others can't.</h4>
                              <p style={{ color: `hsl(${subheadingColor})`, fontFamily: getFontStack(form.font_config?.body), fontWeight: getSupportedBrandFontWeight(form.font_config?.body_weight, "400") }}>Clear visual assurance, wherever your work takes you.</p>
                              <p style={{ color: `hsl(${bodyColor})`, fontFamily: getFontStack(form.font_config?.body), fontWeight: getSupportedBrandFontWeight(form.font_config?.body_weight, "400") }}>Body copy remains clear and comfortable to read.</p>
                            </div>
                          </section>
                        </div>
                        <BrandStudioPreview config={previewConfig} viewport={previewViewport} onViewportChange={setPreviewViewport} title="Brand preview" />
                      </div>
                    </TabsContent>

                    <TabsContent value="landing" className="pt-4">
                      <div className="grid items-start gap-5 xl:grid-cols-[minmax(360px,0.8fr)_minmax(480px,1.2fr)]">
                        <div className="space-y-5">
                          <section className="brand-studio-section space-y-4">
                            <div className="flex items-start gap-3"><span className="brand-studio-section-icon"><Layers3 className="h-4 w-4" /></span><div><h3 className="font-semibold">Hero message</h3><p className="text-xs text-muted-foreground">The first message visitors see.</p></div></div>
                            <div><Label>Headline</Label><Input value={form.landing_content?.hero_title || ""} onChange={(e) => set("landing_content.hero_title", e.target.value)} /></div>
                            <div><Label>Subheading</Label><Input value={form.landing_content?.hero_subheading || ""} onChange={(e) => set("landing_content.hero_subheading", e.target.value)} /></div>
                            <div><Label>Supporting message</Label><Textarea rows={3} value={form.landing_content?.hero_subtitle || ""} onChange={(e) => set("landing_content.hero_subtitle", e.target.value)} /></div>
                            <div className="space-y-2">
                              <Label htmlFor="title-background-graphic">Title background graphic</Label>
                              <div className="flex items-center gap-2">
                                <BrandImageLinkInput key={`graphic-${selected.id}`} id="title-background-graphic" brandId={selected.id} label="Title background graphic" value={form.landing_content?.hero_background_image_url || ""} onChange={(url) => set("landing_content.hero_background_image_url", url)} onUploadingChange={onUploadingChange} />
                                <Tooltip><TooltipTrigger asChild><Button type="button" variant="ghost" size="icon" className="shrink-0" disabled={uploadCount > 0 || !form.landing_content?.hero_background_image_url} onClick={() => set("landing_content.hero_background_image_url", "")} aria-label="Remove title background graphic"><Trash2 className="h-4 w-4" /></Button></TooltipTrigger><TooltipContent>Remove title background graphic</TooltipContent></Tooltip>
                              </div>
                              <div className="flex items-center justify-between gap-3">
                                <Label>Graphic alignment</Label>
                                <div role="group" aria-label="Title graphic alignment" className="flex shrink-0 items-center gap-1">
                                  {GRAPHIC_POSITIONS.map(({ value, label, icon: Icon }) => (
                                    <Tooltip key={value}><TooltipTrigger asChild><Button type="button" variant={getTitleGraphicPosition(form.landing_content?.hero_background_position) === value ? "secondary" : "ghost"} size="icon" className="h-8 w-8" disabled={!form.landing_content?.hero_background_image_url} aria-label={label} aria-pressed={getTitleGraphicPosition(form.landing_content?.hero_background_position) === value} onClick={() => set("landing_content.hero_background_position", value)}><Icon className="h-4 w-4" /></Button></TooltipTrigger><TooltipContent>{label}</TooltipContent></Tooltip>
                                  ))}
                                </div>
                              </div>
                              <div className="flex items-center justify-between"><Label htmlFor="title-graphic-opacity">Graphic opacity</Label><span className="text-sm tabular-nums">{form.landing_content?.hero_background_opacity ?? 25}%</span></div>
                              <Slider id="title-graphic-opacity" aria-label="Graphic opacity" min={0} max={100} step={1} disabled={!form.landing_content?.hero_background_image_url} value={[form.landing_content?.hero_background_opacity ?? 25]} onValueChange={([value]) => set("landing_content.hero_background_opacity", value)} />
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2"><div><Label>Main button</Label><Input value={form.landing_content?.cta_label || ""} onChange={(e) => set("landing_content.cta_label", e.target.value)} /></div><div><Label>Header sign-in button</Label><Input value={form.landing_content?.login_label || ""} onChange={(e) => set("landing_content.login_label", e.target.value)} /></div></div>
                          </section>

                          <section className="brand-studio-section space-y-4">
                            <div className="flex items-start gap-3"><span className="brand-studio-section-icon"><Image className="h-4 w-4" /></span><div><h3 className="font-semibold">Main image</h3><p className="text-xs text-muted-foreground">Use a wide, high-quality image that shows the product or service.</p></div></div>
                            <div><Label>Image link</Label><BrandImageLinkInput key={`main-${selected.id}`} brandId={selected.id} label="Main image" value={form.landing_content?.hero_image_url || ""} onChange={(url) => set("landing_content.hero_image_url", url)} onUploadingChange={onUploadingChange} /></div>
                          </section>

                          <section className="brand-studio-section space-y-4">
                            <div className="flex items-center justify-between gap-3"><div className="flex items-start gap-3"><span className="brand-studio-section-icon"><Layers3 className="h-4 w-4" /></span><div><h3 className="font-semibold">Highlights</h3><p className="text-xs text-muted-foreground">Three concise, image-led points work best.</p></div></div><Button type="button" size="sm" variant="outline" onClick={() => setFeatures([...features, { title: "", description: "", image_url: "" }])}><Plus className="h-4 w-4" /> Add</Button></div>
                            <div className="space-y-2">
                              <Label>Layout style</Label>
                              <div className="grid grid-cols-2 gap-2">
                                {HIGHLIGHT_LAYOUTS.map((layout) => {
                                  const Icon = layout.icon;
                                  const selectedLayout = (form.landing_content?.highlights_layout || "alternating") === layout.value;
                                  return (
                                    <Button
                                      key={layout.value}
                                      type="button"
                                      variant={selectedLayout ? "default" : "outline"}
                                      className="h-auto min-h-16 justify-start whitespace-normal px-3 py-3 text-left"
                                      onClick={() => set("landing_content.highlights_layout", layout.value)}
                                    >
                                      <Icon className="h-4 w-4 shrink-0" />
                                      <span>{layout.label}</span>
                                    </Button>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Spacing</Label>
                              <div className="grid grid-cols-3 gap-2">
                                {HIGHLIGHT_SPACING.map((spacing) => {
                                  const selectedSpacing = (form.landing_content?.highlights_spacing || "standard") === spacing.value;
                                  return (
                                    <Button
                                      key={spacing.value}
                                      type="button"
                                      size="sm"
                                      variant={selectedSpacing ? "default" : "outline"}
                                      onClick={() => set("landing_content.highlights_spacing", spacing.value)}
                                    >
                                      {spacing.label}
                                    </Button>
                                  );
                                })}
                              </div>
                            </div>
                            {features.length === 0 && <Button type="button" variant="outline" className="brand-feature-empty" onClick={() => setFeatures([{ title: "", description: "", image_url: "" }])}><Plus className="h-4 w-4" /> Add your first highlight</Button>}
                            {features.map((feature, index) => (
                              <div key={index} className="brand-feature-editor">
                                <div className="flex items-center justify-between"><span className="text-xs font-semibold text-muted-foreground">HIGHLIGHT {String(index + 1).padStart(2, "0")}</span><Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={uploadCount > 0} onClick={() => setFeatures(features.filter((_, itemIndex) => itemIndex !== index))} aria-label={`Remove highlight ${index + 1}`}><Trash2 className="h-4 w-4" /></Button></div>
                                <Input placeholder="Title" value={feature.title || ""} onChange={(e) => setFeatures(features.map((item, itemIndex) => itemIndex === index ? { ...item, title: e.target.value } : item))} />
                                <div className="flex items-center justify-between gap-3">
                                  <Label>Text alignment</Label>
                                  <div className="flex gap-1" role="group" aria-label={`Highlight ${index + 1} text alignment`}>
                                    {HIGHLIGHT_ALIGNMENTS.map(({ value, label, icon: Icon }) => (
                                      <Tooltip key={value}><TooltipTrigger asChild><Button type="button" variant={(feature.text_alignment || "left") === value ? "secondary" : "ghost"} size="icon" className="h-8 w-8" aria-label={label} aria-pressed={(feature.text_alignment || "left") === value} onClick={() => setFeatures(features.map((item, itemIndex) => itemIndex === index ? { ...item, text_alignment: value } : item))}><Icon className="h-4 w-4" /></Button></TooltipTrigger><TooltipContent>{label}</TooltipContent></Tooltip>
                                    ))}
                                  </div>
                                </div>
                                <BrandImageLinkInput key={`highlight-${selected.id}-${index}`} brandId={selected.id} label={`Highlight ${index + 1} image`} value={feature.image_url || ""} onChange={(url) => setFeatures(features.map((item, itemIndex) => itemIndex === index ? { ...item, image_url: url } : item))} onUploadingChange={onUploadingChange} />
                                <Textarea rows={2} placeholder="Short description" value={feature.description || ""} onChange={(e) => setFeatures(features.map((item, itemIndex) => itemIndex === index ? { ...item, description: e.target.value } : item))} />
                              </div>
                            ))}
                          </section>

                          <section className="brand-studio-section"><Label>Footer text</Label><Input value={form.landing_content?.footer_text || ""} onChange={(e) => set("landing_content.footer_text", e.target.value)} /></section>
                        </div>
                        <BrandStudioPreview config={previewConfig} viewport={previewViewport} onViewportChange={setPreviewViewport} title="Landing page preview" />
                      </div>
                    </TabsContent>

                    <TabsContent value="emails" className="pt-4">
                      <div className="grid items-start gap-5 xl:grid-cols-2">
                        <Card><CardContent className="grid gap-4 pt-6">
                          <p className="text-sm text-muted-foreground">
                            Used for invitation, sign-in link and password reset emails sent to this organisation's users. Changes go live when you publish.
                          </p>
                          <div><Label>Sender name</Label><Input placeholder={form.company_name || "Company name"} value={form.email?.from_name || ""} onChange={(e) => set("email.from_name", e.target.value)} /></div>
                          <div><Label>Sender address (needs a verified email domain)</Label><Input placeholder="noreply@partner.com" value={form.email?.from_address || ""} onChange={(e) => set("email.from_address", e.target.value)} /></div>
                          <div><Label>Reply-to address</Label><Input placeholder={form.support_email || "support@partner.com"} value={form.email?.reply_to || ""} onChange={(e) => set("email.reply_to", e.target.value)} /></div>
                          <div><Label>Email logo link (defaults to the brand logo)</Label><BrandImageLinkInput key={`email-logo-${selected.id}`} brandId={selected.id} label="Email logo" value={form.email?.logo_url || ""} onChange={(url) => set("email.logo_url", url)} onUploadingChange={onUploadingChange} /></div>
                          <div><Label>Footer text</Label><Input value={form.email?.footer_text || ""} onChange={(e) => set("email.footer_text", e.target.value)} /></div>
                          <div className="flex items-center gap-2">
                            <Switch checked={selected.email_domain_verified} onCheckedChange={(v) => updateRow.mutate({ email_domain_verified: v })} />
                            <span className="text-sm">Email domain verified</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Only switch this on once the sender's domain is verified in Resend. Until then emails go out from the Snapshot Live address, shown as "{(form.email?.from_name || form.company_name || "Brand")} via Snapshot Live".
                          </p>
                        </CardContent></Card>
                        <Card><CardContent className="pt-6">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <Label>Email preview</Label>
                            <select aria-label="Choose email to preview" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={emailKind} onChange={(e) => setEmailKind(e.target.value as EmailKind)}>
                              <option value="invite">Invitation</option>
                              <option value="magiclink">Magic link sign-in</option>
                              <option value="recovery">Password reset</option>
                              <option value="camera_alert">Camera alert</option>
                              <option value="camera_image">New camera image</option>
                            </select>
                          </div>
                          {(emailKind === "camera_alert" || emailKind === "camera_image") && (
                            <div className="grid gap-3 sm:grid-cols-2 mb-3">
                              <p className="sm:col-span-2 text-xs text-muted-foreground">Leave blank to use the default wording from Email Design. Placeholders: {"{{camera_name}}"}, {"{{ai_subject}}"}, {"{{organisation}}"}.</p>
                              <div className="sm:col-span-2"><Label>Subject line</Label><Input value={form.email?.[emailKind]?.subject || ""} onChange={(e) => set(`email.${emailKind}.subject`, e.target.value)} placeholder={emailKind === "camera_alert" ? "Alert: {{ai_subject}} – {{camera_name}}" : "{{ai_subject}} – {{camera_name}}"} /></div>
                              <div><Label>Badge text</Label><Input value={form.email?.[emailKind]?.badge_text || ""} onChange={(e) => set(`email.${emailKind}.badge_text`, e.target.value)} placeholder={emailKind === "camera_alert" ? "ALERT" : "NEW IMAGE"} /></div>
                              <ColorPicker label="Badge background" value={form.email?.[emailKind]?.badge_background || defaultCameraCopy[emailKind]?.badge_background || form.color_tokens?.accent || "71 73% 76%"} onChange={(v) => set(`email.${emailKind}.badge_background`, v)} />
                              <ColorPicker label="Badge text colour" value={form.email?.[emailKind]?.badge_text_color || defaultCameraCopy[emailKind]?.badge_text_color || form.color_tokens?.["primary-foreground"] || "0 0% 100%"} onChange={(v) => set(`email.${emailKind}.badge_text_color`, v)} />
                              <div><Label>Button text</Label><Input value={form.email?.[emailKind]?.cta_text || ""} onChange={(e) => set(`email.${emailKind}.cta_text`, e.target.value)} placeholder="View camera" /></div>
                              <div className="sm:col-span-2"><Label>Intro text</Label><Input value={form.email?.[emailKind]?.intro || ""} onChange={(e) => set(`email.${emailKind}.intro`, e.target.value)} /></div>
                              <div><Label>Footer</Label><Input value={form.email?.[emailKind]?.footer || ""} onChange={(e) => set(`email.${emailKind}.footer`, e.target.value)} /></div>
                              <div><Label>Stop-alerts link text</Label><Input value={form.email?.[emailKind]?.unsubscribe_text || ""} onChange={(e) => set(`email.${emailKind}.unsubscribe_text`, e.target.value)} placeholder="Stop these alerts" /></div>
                            </div>
                          )}
                           <iframe title="Email preview" className="w-full h-[560px] rounded-md border bg-card" srcDoc={emailPreviewHtml(form, emailKind, defaultCameraCopy[emailKind])} />
                        </CardContent></Card>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
